// src/redux/slices/surveySlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { resetStore } from './resetStore';
import { getServerUrl, getAuthHeaders } from '../../Utils/serverSelector';

// ====== safe logs ======
const CAN_LOG = false;
const LOG = (...args) => { if (CAN_LOG) console.log('[SURVEY]', ...args); };
const ERR = (...args) => { if (CAN_LOG) console.error('[SURVEY]', ...args); };

/* ---------- helpers (точно как в specialitiesSlice) ---------- */
const authJsonConfig = async () => {
  const headers = await getAuthHeaders();
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true,
  };
};

const authJsonConfigWithJson = async () => {
  const base = await authJsonConfig();
  return {
    ...base,
    headers: {
      ...base.headers,
      'Content-Type': 'application/json',
    },
  };
};

/* ---------- initial ---------- */
const initialState = {
  questions: [],
  status: 'idle',
  error: null,
};

/* ====== GET /surveyQuestions/:uid ====== */
export const fetchSurveyQuestions = createAsyncThunk(
  'survey/fetchSurveyQuestions',
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');
      const cfg = await authJsonConfig();
      const url = `${getServerUrl()}/surveyQuestions/${uid}`;
      LOG('fetchSurveyQuestions:GET', url);

      const res = await axios.get(url, cfg);

      if (res.status === 404) return [];
      if (res.status !== 200) {
        throw new Error(
          res.data?.message || res.data?.error || `HTTP ${res.status}`
        );
      }

      return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
      ERR('fetchSurveyQuestions:error', e?.message);
      return rejectWithValue(e?.response?.data || e?.message || 'fetchSurveyQuestions failed');
    }
  }
);

/* ====== POST /surveyQuestions ====== */
export const addSurveyQuestion = createAsyncThunk(
  'survey/addSurveyQuestion',
  async (questionData, { rejectWithValue }) => {
    try {
      const cfg = await authJsonConfigWithJson();
      const url = `${getServerUrl()}/surveyQuestions`;
      LOG('addSurveyQuestion:POST', url);

      const res = await axios.post(url, questionData, cfg);

      if (res.status !== 200 && res.status !== 201) {
        throw new Error(
          res.data?.message || res.data?.error || `HTTP ${res.status}`
        );
      }

      LOG('addSurveyQuestion:response', { id: res.data?.id });
      return res.data;
    } catch (e) {
      ERR('addSurveyQuestion:error', e?.message);
      return rejectWithValue(e?.response?.data || e?.message || 'addSurveyQuestion failed');
    }
  }
);

/* ====== PUT /surveyQuestions/:id ====== */
export const updateSurveyQuestion = createAsyncThunk(
  'survey/updateSurveyQuestion',
  async (payload, { rejectWithValue }) => {
    try {
      const { id, ...body } = payload || {};
      if (!id) throw new Error('ID не передан');

      const cfg = await authJsonConfigWithJson();
      const url = `${getServerUrl()}/surveyQuestions/${encodeURIComponent(id)}`;
      LOG('updateSurveyQuestion:PUT', url);

      const res = await axios.put(url, body, cfg);

      if (res.status !== 200) {
        throw new Error(
          res.data?.message || res.data?.error || `HTTP ${res.status}`
        );
      }

      LOG('updateSurveyQuestion:response', { id: res.data?.id });
      return res.data;
    } catch (e) {
      ERR('updateSurveyQuestion:error', e?.message);
      return rejectWithValue(e?.response?.data || e?.message || 'updateSurveyQuestion failed');
    }
  }
);

/* ====== DELETE /surveyQuestions/:id ====== */
export const deleteSurveyQuestion = createAsyncThunk(
  'survey/deleteSurveyQuestion',
  async (id, { rejectWithValue }) => {
    try {
      if (!id) throw new Error('ID не передан');

      const cfg = await authJsonConfig();
      const url = `${getServerUrl()}/surveyQuestions/${encodeURIComponent(id)}`;
      LOG('deleteSurveyQuestion:DELETE', url);

      const res = await axios.delete(url, cfg);

      if (res.status !== 200) {
        throw new Error(
          res.data?.message || res.data?.error || `HTTP ${res.status}`
        );
      }

      LOG('deleteSurveyQuestion:response', { id });
      return res.data?.id ?? id;
    } catch (e) {
      ERR('deleteSurveyQuestion:error', e?.message);
      return rejectWithValue(e?.response?.data || e?.message || 'deleteSurveyQuestion failed');
    }
  }
);

/* ====== GET /public/:uid/survey-questions (без авторизации) ====== */
export const fetchPublicSurveyQuestions = createAsyncThunk(
  'survey/fetchPublicSurveyQuestions',
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error('UID не передан');
      const base = getServerUrl().replace(/\/$/, '');
      const url = `${base}/public/${uid}/survey-questions`;
      LOG('fetchPublicSurveyQuestions:GET', url);

      const res = await axios.get(url, { validateStatus: () => true });

      if (res.status === 404) return [];
      if (res.status !== 200) {
        throw new Error(
          res.data?.message || res.data?.error || `HTTP ${res.status}`
        );
      }

      return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
      ERR('fetchPublicSurveyQuestions:error', e?.message);
      return rejectWithValue(e?.response?.data || e?.message || 'fetchPublicSurveyQuestions failed');
    }
  }
);

/* ====== slice ====== */
const surveySlice = createSlice({
  name: 'survey',
  initialState,
  reducers: {},
  extraReducers: (builder) => {

    // FETCH
    builder
      .addCase(fetchSurveyQuestions.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        LOG('state -> loading (fetchSurveyQuestions)');
      })
      .addCase(fetchSurveyQuestions.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.questions = action.payload || [];
        LOG('state -> succeeded (fetchSurveyQuestions)', { count: state.questions.length });
      })
      .addCase(fetchSurveyQuestions.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Fetch survey questions failed';
        state.questions = [];
        ERR('state -> failed (fetchSurveyQuestions)', state.error);
      });

    // ADD
    builder
      .addCase(addSurveyQuestion.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        LOG('state -> loading (addSurveyQuestion)');
      })
      .addCase(addSurveyQuestion.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.payload) state.questions.push(action.payload);
        LOG('state -> succeeded (addSurveyQuestion)', {
          newId: action.payload?.id,
          total: state.questions.length,
        });
      })
      .addCase(addSurveyQuestion.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Add survey question failed';
        ERR('state -> failed (addSurveyQuestion)', state.error);
      });

    // UPDATE
    builder
      .addCase(updateSurveyQuestion.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        LOG('state -> loading (updateSurveyQuestion)');
      })
      .addCase(updateSurveyQuestion.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const updated = action.payload;
        const idx = state.questions.findIndex((q) => q.id === updated?.id);
        if (idx !== -1) {
          state.questions[idx] = { ...state.questions[idx], ...updated };
        } else {
          state.questions.push(updated);
        }
        LOG('state -> succeeded (updateSurveyQuestion)', { id: updated?.id, idx });
      })
      .addCase(updateSurveyQuestion.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Update survey question failed';
        ERR('state -> failed (updateSurveyQuestion)', state.error);
      });

    // DELETE
    builder
      .addCase(deleteSurveyQuestion.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        LOG('state -> loading (deleteSurveyQuestion)');
      })
      .addCase(deleteSurveyQuestion.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.questions = state.questions.filter((q) => q.id !== action.payload);
        LOG('state -> succeeded (deleteSurveyQuestion)', {
          removedId: action.payload,
          total: state.questions.length,
        });
      })
      .addCase(deleteSurveyQuestion.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Delete survey question failed';
        ERR('state -> failed (deleteSurveyQuestion)', state.error);
      });

    // FETCH PUBLIC
    builder
      .addCase(fetchPublicSurveyQuestions.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        LOG('state -> loading (fetchPublicSurveyQuestions)');
      })
      .addCase(fetchPublicSurveyQuestions.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.questions = action.payload || [];
        LOG('state -> succeeded (fetchPublicSurveyQuestions)', { count: state.questions.length });
      })
      .addCase(fetchPublicSurveyQuestions.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Fetch public survey questions failed';
        state.questions = [];
        ERR('state -> failed (fetchPublicSurveyQuestions)', state.error);
      });

    // RESET
    builder.addCase(resetStore, () => initialState);
  },
});

export default surveySlice.reducer;